import { useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Panel from '@/components/ui/Panel';
import Toast from '@/components/ui/Toast';
import { aed, shortDate } from '@/lib/format';
import { portalOrders, portalPartner, portalProducts, type PortalOrderStatus } from '@/mocks/portal';

const STEP_ORDER: PortalOrderStatus[] = [
  'New',
  'Confirmed',
  'AwaitingPayment',
  'Paid',
  'ReadyForPickup',
  'PickedUp',
  'Closed',
];

const stepLabelKey: Record<PortalOrderStatus, string> = {
  New: 'portal.order.stepper.new',
  Confirmed: 'portal.order.stepper.confirmed',
  AwaitingPayment: 'portal.order.stepper.awaiting',
  Paid: 'portal.order.stepper.paid',
  ReadyForPickup: 'portal.order.stepper.ready',
  PickedUp: 'portal.order.stepper.picked',
  Closed: 'portal.order.stepper.closed',
};

export default function PortalOrderDetailPage() {
  const { t } = useTranslation();
  const { poNumber } = useParams<{ poNumber: string }>();
  const order = portalOrders.find((o) => o.po === poNumber);

  const [status, setStatus] = useState<PortalOrderStatus>(order?.status || 'New');
  const [readyDate, setReadyDate] = useState(order?.readyDate || '');
  const [showReady, setShowReady] = useState(false);
  const [showReplacement, setShowReplacement] = useState(false);
  const [replacementSku, setReplacementSku] = useState(portalProducts[0].sku);
  const [documents, setDocuments] = useState(order?.documents || []);
  const [thread, setThread] = useState(order?.thread || []);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState<{ message: string; detail?: string } | null>(null);

  if (!order) {
    return (
      <div>
        <Link to="/portal/orders" className="inline-flex items-center gap-1 text-[12px] font-medium text-foreground-600 hover:text-primary-700">
          <i className="ri-arrow-left-line text-[13px]" />
          {t('portal.order.back')}
        </Link>
        <p className="mt-6 rounded-lg border border-background-200 bg-background-50 px-4 py-10 text-center text-[13px] text-foreground-500">
          {t('portal.order.notFound')}
        </p>
      </div>
    );
  }

  const total = order.lines.reduce((s, l) => s + l.qty * l.price, 0);
  const units = order.lines.reduce((s, l) => s + l.qty, 0);
  const currentStep = STEP_ORDER.indexOf(status);

  const confirmOrder = () => {
    setStatus('Confirmed');
    setToast({ message: t('portal.order.confirmedToast', { po: order.po }) });
  };

  const saveReadyDate = () => {
    setShowReady(false);
    if (!readyDate) return;
    setToast({ message: t('portal.order.readyToast', { date: shortDate(readyDate) }) });
  };

  const markReady = () => {
    setStatus('ReadyForPickup');
    setToast({ message: t('portal.order.markReadyToast', { po: order.po }) });
  };

  const sendReplacement = () => {
    setShowReplacement(false);
    const sku = replacementSku;
    setToast({ message: t('portal.order.replacementToast'), detail: sku });
  };

  const addDocument = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setDocuments((prev) => [...prev, { name: file.name, kind: 'delivery' }]);
    setToast({ message: t('portal.order.uploaded', { name: file.name }) });
    event.target.value = '';
  };

  const sendMessage = () => {
    const body = message.trim();
    if (!body) return;
    setThread((prev) => [
      ...prev,
      {
        id: `local-${prev.length + 1}`,
        author: 'partner',
        name: portalPartner.user,
        body,
        when: '2026-10-24 10:05',
      },
    ]);
    setMessage('');
  };

  return (
    <div>
      <Link to="/portal/orders" className="inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 hover:text-primary-700">
        <i className="ri-arrow-left-line text-[13px]" />
        {t('portal.order.back')}
      </Link>

      <div className="mb-4 mt-2 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="tnum text-[21px] font-semibold leading-7 text-foreground-950">{order.po}</h1>
            <PortalStatus status={status} />
          </div>
          <p className="mt-1 text-[11.5px] text-foreground-500">
            {t('portal.order.meta', { partner: portalPartner.name, created: shortDate(order.date) })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {status === 'New' && (
            <button
              type="button"
              onClick={confirmOrder}
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-check-line text-[14px]" />
              {t('portal.order.confirm')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowReady((v) => !v)}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-calendar-line text-[13px]" />
            {t('portal.order.ready')}
          </button>
          <button
            type="button"
            onClick={() => setShowReplacement(true)}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-swap-box-line text-[13px]" />
            {t('portal.order.replacement')}
          </button>
          <button
            type="button"
            onClick={markReady}
            disabled={status === 'New' || status === 'ReadyForPickup' || status === 'PickedUp' || status === 'Closed'}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-3 text-[12px] font-semibold text-background-50 hover:bg-foreground-900 disabled:cursor-not-allowed disabled:bg-background-300 disabled:text-foreground-500"
          >
            <i className="ri-truck-line text-[14px]" />
            {t('portal.order.markReady')}
          </button>
        </div>
      </div>

      <Panel padded={false} className="mb-4">
        <div className="overflow-x-auto thin-scroll px-4 py-4">
          <ol className="flex min-w-[720px] items-start">
            {STEP_ORDER.map((step, index) => {
              const done = index < currentStep;
              const active = index === currentStep;
              return (
                <li key={step} className="relative flex flex-1 flex-col items-center text-center">
                  {index > 0 && (
                    <span
                      className={`absolute left-0 top-[9px] h-[2px] w-1/2 ${done || active ? 'bg-primary-400' : 'bg-background-200'}`}
                    />
                  )}
                  {index < STEP_ORDER.length - 1 && (
                    <span
                      className={`absolute right-0 top-[9px] h-[2px] w-1/2 ${done ? 'bg-primary-400' : 'bg-background-200'}`}
                    />
                  )}
                  <span
                    className={`relative z-10 flex h-5 w-5 items-center justify-center rounded-full border text-[9px] font-semibold ${
                      done
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : active
                          ? 'border-primary-500 bg-background-50 text-primary-700 ring-2 ring-primary-200'
                          : 'border-background-300 bg-background-50 text-foreground-400'
                    }`}
                  >
                    {done ? <i className="ri-check-line text-[10px]" /> : index + 1}
                  </span>
                  <span
                    className={`mt-1.5 max-w-[86px] text-[9.5px] leading-3 ${
                      active ? 'font-semibold text-primary-800' : done ? 'font-medium text-foreground-700' : 'text-foreground-400'
                    }`}
                  >
                    {t(stepLabelKey[step])}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
        {readyDate && (
          <div className="flex items-center gap-1.5 border-t border-background-200 px-4 py-2 text-[10.5px] text-foreground-600">
            <i className="ri-calendar-check-line text-[12px] text-primary-600" />
            {t('portal.order.readyDateLabel')}: <span className="tnum font-semibold">{shortDate(readyDate)}</span>
          </div>
        )}
      </Panel>

      {showReady && (
        <Panel className="mb-4 !p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="block min-w-0 flex-1">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('portal.order.readyDateLabel')}</span>
              <input
                type="date"
                value={readyDate}
                onChange={(e) => setReadyDate(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowReady(false)}
                className="h-9 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700"
              >
                {t('portal.common.cancel')}
              </button>
              <button
                type="button"
                onClick={saveReadyDate}
                className="h-9 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50"
              >
                {t('portal.order.readyDateSubmit')}
              </button>
            </div>
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="flex flex-col gap-4 xl:col-span-2">
          <Panel padded={false}>
            <div className="border-b border-background-200 px-4 py-3">
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.order.linesTitle')}</h2>
              <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.order.linesSub')}</p>
            </div>
            <div className="overflow-x-auto thin-scroll">
              <table className="w-full min-w-[620px] border-collapse text-[12px]">
                <thead>
                  <tr className="bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                    <th className="px-4 py-2 font-medium">{t('portal.order.colSku')}</th>
                    <th className="px-3 py-2 font-medium">{t('portal.order.colItem')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('portal.order.colQty')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('portal.order.colUnit')}</th>
                    <th className="px-4 py-2 text-right font-medium">{t('portal.order.colAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map((l) => (
                    <tr key={l.sku} className="border-t border-background-200/70">
                      <td className="tnum px-4 py-2.5 text-[11px] font-semibold text-primary-700">{l.sku}</td>
                      <td className="px-3 py-2.5 text-foreground-800">{l.name}</td>
                      <td className="tnum px-3 py-2.5 text-right text-foreground-800">{l.qty}</td>
                      <td className="tnum px-3 py-2.5 text-right text-foreground-600">{aed(l.price)}</td>
                      <td className="tnum px-4 py-2.5 text-right font-medium text-foreground-950">{aed(l.qty * l.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-background-200 bg-background-100/60 px-4 py-2.5">
              <span className="text-[11.5px] text-foreground-600">
                {t('portal.order.total')} · {t('portal.order.units', { count: units })}
              </span>
              <span className="tnum text-[14px] font-semibold text-foreground-950">{aed(total)}</span>
            </div>
          </Panel>

          <Panel padded={false}>
            <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.order.docsTitle')}</h2>
                <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.order.docsSub')}</p>
              </div>
              <label className="inline-flex h-7 w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap rounded border border-background-300 bg-background-50 px-2 text-[10.5px] font-medium text-foreground-700 hover:bg-background-100">
                <i className="ri-upload-2-line text-[12px]" />
                {t('portal.order.uploadInvoice')}
                <input type="file" accept=".pdf,image/*" onChange={addDocument} className="hidden" />
              </label>
            </div>
            <ul className="divide-y divide-background-200/70">
              {documents.map((d) => (
                <li key={d.name} className="flex items-center justify-between gap-2 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-background-100 text-foreground-500">
                      <i className="ri-file-pdf-2-line text-[14px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[11.5px] font-medium text-foreground-900">{d.name}</p>
                      <p className="text-[9.5px] text-foreground-400">
                        {d.kind === 'invoice' ? t('portal.order.invoice') : t('portal.order.deliveryNote')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label={t('portal.common.download')}
                    className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-800"
                  >
                    <i className="ri-download-2-line text-[13px]" />
                  </button>
                </li>
              ))}
              {documents.length === 0 && (
                <li className="px-4 py-6 text-center text-[11.5px] text-foreground-500">{t('portal.order.docEmpty')}</li>
              )}
            </ul>
            <p className="border-t border-background-200 px-4 py-2 text-[10px] text-foreground-400">{t('portal.order.uploadNote')}</p>
          </Panel>
        </div>

        <div className="flex flex-col gap-4">
          <Panel>
            <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.order.pickupTitle')}</h2>
            <p className="mt-1.5 text-[11.5px] leading-4 text-foreground-600">{t('portal.order.pickupNote')}</p>
            <dl className="mt-3 space-y-2 border-t border-background-200 pt-3 text-[11.5px]">
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500">{t('portal.order.partner')}</dt>
                <dd className="max-w-[170px] text-right font-medium text-foreground-900">{portalPartner.name}</dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500">{t('portal.shell.partnerAccount')}</dt>
                <dd className="max-w-[170px] text-right font-medium text-foreground-900">
                  {portalPartner.warehouse}, {portalPartner.city}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500">{t('portal.order.created')}</dt>
                <dd className="tnum text-right font-medium text-foreground-900">{shortDate(order.date)}</dd>
              </div>
            </dl>
          </Panel>

          <Panel padded={false}>
            <div className="border-b border-background-200 px-4 py-3">
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.order.messagesTitle')}</h2>
              <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.order.messagesSub')}</p>
            </div>
            <div className="max-h-[280px] min-h-[80px] overflow-y-auto thin-scroll px-4 py-3">
              {thread.length === 0 && (
                <p className="py-4 text-center text-[11.5px] text-foreground-500">{t('portal.order.threadEmpty')}</p>
              )}
              <div className="flex flex-col gap-2.5">
                {thread.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[92%] rounded-lg border px-2.5 py-2 ${
                      m.author === 'partner'
                        ? 'ml-auto border-primary-200 bg-primary-50'
                        : 'border-background-200 bg-background-100'
                    }`}
                  >
                    <p className="text-[10px] font-semibold text-foreground-700">{m.name}</p>
                    <p className="mt-0.5 text-[11.5px] leading-4 text-foreground-800">{m.body}</p>
                    <p className="tnum mt-1 text-[9px] text-foreground-400">{m.when}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-background-200 p-2.5">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                rows={2}
                placeholder={t('portal.order.messagePlaceholder')}
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-[11.5px] outline-none focus:border-primary-400"
              />
              <button
                type="button"
                onClick={sendMessage}
                className="mt-1.5 inline-flex h-7 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded bg-foreground-950 text-[11px] font-semibold text-background-50 hover:bg-foreground-900"
              >
                <i className="ri-send-plane-line text-[12px]" />
                {t('portal.order.send')}
              </button>
            </div>
          </Panel>
        </div>
      </div>

      {showReplacement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-[14px] font-semibold text-foreground-950">{t('portal.order.replacementTitle')}</h2>
            <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('portal.order.replacementSub')}</p>
            <label className="mt-3 block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">
                {t('portal.order.replacementSelect')}
              </span>
              <select
                value={replacementSku}
                onChange={(e) => setReplacementSku(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400"
              >
                {portalProducts.map((p) => (
                  <option key={p.sku} value={p.sku}>
                    {p.sku} · {p.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowReplacement(false)}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
              >
                {t('portal.common.cancel')}
              </button>
              <button
                type="button"
                onClick={sendReplacement}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
              >
                {t('portal.order.replacementSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} onClose={() => setToast(null)} />}
    </div>
  );
}